import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useUser } from '../../context/UserContext';
import { productsAPI } from '../../services/api';
import ProductForm from './ProductForm';
import '../../App.css';
import '../admin/AdminComponents.css';

const ProductsManagement = () => {
  const { products, deleteProduct, reorderProducts, refreshData } = useAdmin();
  const { hasPermission } = useUser();
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sortOrder, setSortOrder] = useState('id');
  const [clearingDiscounts, setClearingDiscounts] = useState(false);

  const hasDiscount = (product) => {
    const discountedPrice = parseFloat(product.discountedPrice);
    return Number.isFinite(discountedPrice) && discountedPrice > 0;
  };

  const StatusBadge = ({ active, activeLabel, inactiveLabel, activeTone = 'positive', inactiveTone = 'neutral' }) => (
    <span className={`admin-product-status-badge ${active ? activeTone : inactiveTone}`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );

  const handleEdit = async (product) => {
    try {
      const response = await productsAPI.getById(product.id);
      if (response.success && response.data) {
        setEditingProduct(response.data);
      } else {
        setEditingProduct(product);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setEditingProduct(product);
    }
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
      } catch (error) {
        alert(error.message || 'Failed to delete product');
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
    if (refreshData) {
      refreshData();
    }
  };

  const handleSort = (order) => {
    setSortOrder(order);
  };

  const handleClearDiscounts = async () => {
    const activeDiscountCount = products.filter(hasDiscount).length;

    if (activeDiscountCount === 0) {
      window.alert('There are no active product discounts to disable.');
      return;
    }

    if (!window.confirm(`Disable discounts on ${activeDiscountCount} product${activeDiscountCount === 1 ? '' : 's'}?`)) {
      return;
    }

    setClearingDiscounts(true);
    try {
      const response = await productsAPI.clearDiscounts();
      if (refreshData) {
        await refreshData();
      }
      window.alert(response.message || `Disabled ${response.data?.updatedCount || activeDiscountCount} product discounts.`);
    } catch (error) {
      window.alert(error.message || 'Failed to disable product discounts.');
    } finally {
      setClearingDiscounts(false);
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortOrder) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price-asc':
        return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
      case 'price-desc':
        return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
      case 'category':
        return a.category.localeCompare(b.category);
      default:
        return a.id - b.id;
    }
  });

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Products Management</h2>
          <div className="admin-section-actions">
            <select
              className="admin-form-select"
              value={sortOrder}
              onChange={(e) => handleSort(e.target.value)}
              style={{ width: 'auto', marginRight: '1rem' }}
            >
              <option value="id">Sort by ID</option>
              <option value="name">Sort by Name</option>
              <option value="price-asc">Sort by Price (Low to High)</option>
              <option value="price-desc">Sort by Price (High to Low)</option>
              <option value="category">Sort by Category</option>
            </select>
            {hasPermission('products.create') && (
              <button
                className="admin-button primary"
                onClick={() => {
                  setEditingProduct(null);
                  setShowForm(true);
                }}
              >
                Add Product
              </button>
            )}
          </div>
      </div>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>
                {hasPermission('products.edit') ? (
                  <button
                    type="button"
                    className="admin-table-header-action"
                    onClick={handleClearDiscounts}
                    disabled={clearingDiscounts}
                    title="Disable all active product discounts"
                  >
                    {clearingDiscounts ? 'Disabling...' : 'Discount'}
                  </button>
                ) : 'Discount'}
              </th>
              <th>Textures</th>
              <th>Featured</th>
              <th>New</th>
              <th>Texture Bundle</th>
              <th>Discounted</th>
              <th>Hidden</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map((product) => (
              <tr key={product.id}>
                <td>{product.id}</td>
                <td>{product.name}</td>
                <td>{product.category}</td>
                <td>${(parseFloat(product.price) || 0).toFixed(2)}</td>
                <td>
                  {product.discountedPrice !== null && product.discountedPrice !== undefined && product.discountedPrice !== '' ? `$${(parseFloat(product.discountedPrice) || 0).toFixed(2)}` : '-'}
                </td>
                <td>{product.textures.join(', ')}</td>
                <td>
                  <StatusBadge active={Boolean(product.featured)} activeLabel="Featured" inactiveLabel="Standard" />
                </td>
                <td>
                  <StatusBadge active={Boolean(product.newArrival)} activeLabel="New" inactiveLabel="Standard" />
                </td>
                <td>
                  <StatusBadge
                    active={Boolean(product.bundleEnabled)}
                    activeLabel={`${Number(product.bundleQuantity) || 3} for ${parseFloat(product.bundleDiscountPercent) || 0}% off`}
                    inactiveLabel="Disabled"
                  />
                </td>
                <td>
                  <StatusBadge active={hasDiscount(product)} activeLabel="Discounted" inactiveLabel="Full Price" />
                </td>
                <td>
                  <StatusBadge
                    active={Boolean(product.hidden)}
                    activeLabel="Hidden"
                    inactiveLabel="Visible"
                    activeTone="negative"
                    inactiveTone="positive"
                  />
                </td>
                <td>
                  <div className="admin-table-actions">
                    {hasPermission('products.edit') && (
                      <button
                        className="admin-icon-button"
                        onClick={() => handleEdit(product)}
                        title="Edit"
                      >
                        ✎
                      </button>

                    )}
                    {hasPermission('products.delete') && (
                      <button
                        className="admin-icon-button danger"
                        onClick={() => handleDelete(product.id)}
                        title="Delete"
                      >
                        ×
                      </button>

                    )}
                  </div>

                </td>

              </tr>

            ))}
          </tbody>

        </table>

      </div>


      {showForm && (
        <ProductForm
          product={editingProduct}
          onClose={handleFormClose}
        />

      )}
    </div>

  );
};

export default ProductsManagement;
